import { useEffect } from "react";
import { store } from "@store/index";
import { selectUserOrgId, selectUserRole } from "@utils/redux-utils/selectors/userSelectors";
import { getChatbaseIdentity, sendHelpRequest } from "@utils/api-communication/RestApiCli";
import { buildSupportDeviceContext } from "@utils/supportContext";
import { createSupportTicketTool } from "./createSupportTicketTool";

declare global {
  interface Window {
    chatbase?: any;
    chatbaseUserConfig?: Record<string, unknown>;
  }
}

function buildUnsignedSupportAttributes(): Record<string, string> {
  const state = store.getState();
  const device = buildSupportDeviceContext();

  const attributes: Record<string, unknown> = {
    orgId: selectUserOrgId(state),
    role: selectUserRole(state),
    ...(device
      ? {
          headsetModel: device.headsetModel,
          headsetAppVersion: device.headsetAppVersion,
          headsetOsVersion: device.headsetOsVersion,
        }
      : {}),
  };

  return Object.fromEntries(
    Object.entries(attributes)
      .map(([k, v]) => [k, typeof v === "string" ? v.trim() : ""] as const)
      .filter(([, v]) => v.length > 0),
  );
}

const REIDENTIFY_DEBOUNCE_MS = 1000;

function supportContextKey(): string {
  return Object.entries(buildUnsignedSupportAttributes())
    .map(([k, v]) => `${k}=${v}`)
    .join("|");
}

export default function ChatbaseWidget() {
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    // Without a bot id the embed script has nothing to attach to; injecting it
    // anyway leaves a <script id="undefined"> and a floating bubble that swallows
    // clicks. Environments that do not configure the bot simply get no widget.
    const botId = process.env.REACT_APP_CHATBOT_IDENTITY_SECRET;
    if (!botId) {
      return;
    }

    let isCancelled = false;
    let unsubscribe: (() => void) | undefined;
    let reidentifyTimer: ReturnType<typeof setTimeout> | undefined;
    let identity: Awaited<ReturnType<typeof getChatbaseIdentity>> | undefined;

    const callChatbase = (action: string, ...args: unknown[]) =>
      window.chatbase ? window.chatbase(action, ...args) : undefined;

    const shouldBootstrap = () => callChatbase("getState") !== "initialized";

    const bootstrapQueue = () => {
      if (!shouldBootstrap()) {
        return;
      }

      const queueingFn: any = (...args: unknown[]) => {
        queueingFn.q = queueingFn.q || [];
        queueingFn.q.push(args);
      };

      window.chatbase = new Proxy(queueingFn, {
        get(target, prop) {
          if (prop === "q") {
            return target.q;
          }
          return (...args: unknown[]) => target(prop, ...args);
        },
      });
    };

    const injectScript = () => {
      const BOT_ID = botId;
      const DOMAIN = "www.chatbase.co";

      if (document.getElementById(BOT_ID)) {
        return;
      }

      const script = document.createElement("script");

      // This is the local script file downloaded from https://www.chatbase.co/embed.min.js
      // Everytime we download the script, we need to update the integrity hash using the following command:
      // openssl dgst -sha384 -binary public/scripts/embed.min.js | openssl base64 -A
      // Also the script has a guard to prevent it from local usage, so we need to remove the guard.
      // Search &&(s=r.origin) inside the public/scripts/embed.min.js file and remove it.
      // You need to create integrity after removing the guard.

      script.src = "/scripts/embed.min.js";

      script.integrity = "sha384-jqvnTY11AphLKGY1vS022n+ksHr2NZ30RpkrcZM2uM5DX929GGPCHYSomN6GKpTM";
      script.crossOrigin = "anonymous";

      script.id = BOT_ID;

      (script as any).domain = DOMAIN;

      script.setAttribute("domain", DOMAIN);

      script.setAttribute("data-domain", DOMAIN);

      document.body.appendChild(script);
    };

    const loadIdentityConfig = async () => {
      try {
        identity = await getChatbaseIdentity();

        if (!identity.token || isCancelled) {
          return;
        }

        window.chatbaseUserConfig = {
          ...(identity.publicAttributes ?? {}),
          ...buildUnsignedSupportAttributes(),
          token: identity.token,
        };
      } catch (error) {
        console.error("Failed to identify Chatbase user", error);
      }
    };

    const reidentify = () => {
      if (!identity?.token || isCancelled) {
        return;
      }
      callChatbase("identify", {
        ...(identity.publicAttributes ?? {}),
        ...buildUnsignedSupportAttributes(),
        token: identity.token,
      });
    };

    const rememberSupportEmail = (supportEmail: string) => {
      if (!identity) {
        return;
      }
      identity = {
        ...identity,
        publicAttributes: { ...(identity.publicAttributes ?? {}), supportEmail },
      };
      reidentify();
    };

    const createSupportTicket = (message: string, email?: string) =>
      new Promise<string | undefined>((resolve, reject) => {
        sendHelpRequest(
          message,
          email,
          (response) => resolve(response?.data?.ticketNumber),
          (error) => reject(error),
          buildSupportDeviceContext(),
          "chatbase",
        );
      });

    const registerSupportTools = () => {
      callChatbase("registerTools", {
        createSupportTicket: createSupportTicketTool({
          sendTicket: createSupportTicket,
          rememberEmail: rememberSupportEmail,
        }),
      });
    };

    const watchSupportContext = () => {
      let { product, user } = store.getState();
      let lastKey = supportContextKey();

      unsubscribe = store.subscribe(() => {
        const state = store.getState();
        if (state.product === product && state.user === user) {
          return;
        }
        product = state.product;
        user = state.user;

        const key = supportContextKey();
        if (key === lastKey) {
          return;
        }
        lastKey = key;
        clearTimeout(reidentifyTimer);
        reidentifyTimer = setTimeout(reidentify, REIDENTIFY_DEBOUNCE_MS);
      });
    };

    const onLoad = async () => {
      bootstrapQueue();
      registerSupportTools();
      await loadIdentityConfig();
      if (isCancelled) {
        return;
      }
      injectScript();
      watchSupportContext();
    };

    const handleWindowLoad = () => {
      void onLoad();
    };

    if (document.readyState === "complete") {
      void onLoad();
    } else {
      window.addEventListener("load", handleWindowLoad);
    }

    return () => {
      isCancelled = true;
      window.removeEventListener("load", handleWindowLoad);
      clearTimeout(reidentifyTimer);
      unsubscribe?.();
    };
  }, []);

  return null;
}
